import { At } from "@styled-icons/boxicons-regular";
import { XCircle } from "@styled-icons/boxicons-solid";
import { observer } from "mobx-react-lite";
import { Channel, Message } from "revolt.js";
import styled from "styled-components/macro";
import { Text } from "preact-i18n";
import { StateUpdater, useEffect } from "preact/hooks";

import { IconButton } from "@revoltchat/ui";

import { internalSubscribe } from "../../../../lib/eventEmitter";

import { useApplicationState } from "../../../../mobx/State";
import { SECTION_MENTION } from "../../../../mobx/stores/Layout";
import { Reply } from "../../../../mobx/stores/MessageQueue";

import Tooltip from "../../../common/Tooltip";
import { PreviewContent, ReplyBase } from "../attachments/MessageReply";

interface Props {
    channel: Channel;
    replies: Reply[];
    setReplies: StateUpdater<Reply[]>;
}

const Base = styled.div`
    @keyframes bottomBounce {
        0% {
            transform: translateY(33px);
        }
        100% {
            transform: translateY(0px);
        }
    }

    display: flex;
    height: 30px;
    padding: 0 20px;
    user-select: none;
    align-items: center;
    background: var(--secondary-background);
    animation: bottomBounce 340ms cubic-bezier(0.2, 0.9, 0.5, 1.16) forwards;

    > div {
        flex-grow: 1;
        margin-bottom: 0;

        &::before {
            display: none;
        }
    }

    .replyto {
        align-self: center;
        font-weight: 500;
        flex-shrink: 0;
    }

    .content {
        display: flex;
        pointer-events: none;

        .username {
            display: flex;
            align-items: center;
            gap: 6px;
            font-weight: 600;
            flex-shrink: 0;
        }

        .message {
            display: flex;
            max-height: 26px;
            gap: 4px;
        }
    }

    .actions {
        gap: 12px;
        display: flex;
    }

    .toggle {
        gap: 2px;
        display: flex;
        font-size: 12px;
        align-items: center;
        font-weight: 800;
        text-transform: uppercase;
        min-width: 6ch;
        padding: 1px 4px;
        border-radius: var(--border-radius);
        background: transparent;
    }

    .toggle.on {
        background: var(--warning);
    }

    /*@media (pointer: coarse) { //FIXME: Make action buttons bigger on pointer coarse
        .actions > svg {
            height: 25px;
        }
    }*/
`;

// ! FIXME: Move to global config
const MAX_REPLIES = 5;
export default observer(({ channel, replies, setReplies }: Props) => {
    const client = channel.client;
    const layout = useApplicationState().layout;

    // Event listener for adding new messages to reply bar.
    useEffect(() => {
        return internalSubscribe("ReplyBar", "add", (_message) => {
            const message = _message as Message;
            if (
                replies.length >= MAX_REPLIES ||
                replies.find((x) => x.id === message._id)
            )
                return;

            setReplies([
                ...replies,
                {
                    id: message._id,
                    mention:
                        message.author_id === client.user!._id
                            ? false
                            : layout.getSectionState(SECTION_MENTION, false),
                },
            ]);
        });
    }, [replies, setReplies, client.user]);

    // Map all the replies to messages we are aware of.
    const messages = replies.map((x) => client.messages.get(x.id));

    // Remove any replies which don't resolve to valid messages.
    useEffect(() => {
        if (messages.includes(undefined)) {
            setReplies(
                replies.filter((_, i) => typeof messages[i] !== "undefined"),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages, replies, setReplies]);

    return (
        <div>
            {replies.map((reply, index) => {
                const message = messages[index];
                if (!message) return null;

                return (
                    <Base key={reply.id}>
                        <ReplyBase preview>
                            <div className="replyto">
                                <Text id="app.main.channel.reply.replying" />
                            </div>
                            <PreviewContent message={message} userSize={16} />
                        </ReplyBase>
                        <span className="actions">
                            {message.author_id !== client.user!._id && (
                                <IconButton
                                    onClick={() => {
                                        let state = false;
                                        setReplies(
                                            replies.map((_, i) => {
                                                if (i === index) {
                                                    state = !_.mention;
                                                    return {
                                                        ..._,
                                                        mention: !_.mention,
                                                    };
                                                }

                                                return _;
                                            }),
                                        );

                                        layout.setSectionState(
                                            SECTION_MENTION,
                                            state,
                                            false,
                                        );
                                    }}>
                                    <Tooltip
                                        content={
                                            <Text id="app.main.channel.reply.toggle" />
                                        }>
                                        <span
                                            className={
                                                reply.mention
                                                    ? "toggle on"
                                                    : "toggle"
                                            }>
                                            <At size={15} />
                                            <Text
                                                id={
                                                    reply.mention
                                                        ? "general.on"
                                                        : "general.off"
                                                }
                                            />
                                        </span>
                                    </Tooltip>
                                </IconButton>
                            )}
                            <IconButton
                                onClick={() =>
                                    setReplies(
                                        replies.filter((_, i) => i !== index),
                                    )
                                }>
                                <XCircle size={16} />
                            </IconButton>
                        </span>
                    </Base>
                );
            })}
        </div>
    );
});
